import React, { useState, useLayoutEffect } from 'react'
import { useHistory } from 'react-router'
import { useSelector, useDispatch } from 'react-redux'
import * as fromBlockActions from 'store/actions/blocks.action'
import * as fromLessonActions from 'store/actions/lessons-admin.action'
import { IMainState } from 'store/states'
import { ILessonModel } from 'models/lesson.model'
import { IBlockInstanceModel, initialState } from 'models/block.model'
import {
  prepareBlockSurveyBlockData,
  prepareBlockRichTextData,
  prepareBlockTaskData,
  prepareBlockMultimediaData,
  prepareBlockScormData,
  prepareBlockMeetingData,
} from 'helpers/converters.helper'
import { BlockTypes } from 'enum'
import lessonBlocksList from 'api/administration/blocks/List'
import { destroyEvaluation } from 'api/administration/blocks/Destroy'

const defaultValues = {
  name: '',
}

const useActions = ({
  lessonId,
  onFinish,
  onEdit,
}: {
  lessonId: number | string
  onFinish: () => void
  onEdit: () => void
}) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const { list } = useSelector(({ blocks }: IMainState) => blocks)
  const { edit } = useSelector(({ lessons }: IMainState) => lessons)
  const [selectedOption, setSelectedOption] = useState<BlockTypes | null>(null)
  const [lessonData, setLessonData] = useState(defaultValues as ILessonModel)
  const [order, setOrder] = useState([initialState])
  const [selected, setSelected] = useState(0)
  const [loading, setLoading] = useState(false)
  const [draggable, setDraggable] = useState(false)

  useLayoutEffect(() => {
    setLessonData({ ...defaultValues, ...edit })
  }, [edit])

  const handleSelectedOptionChange = (option: BlockTypes) => {
    let newBlock: any
    switch (option) {
      case BlockTypes.BlockSurvey:
        newBlock = prepareBlockSurveyBlockData(lessonId, list.length + 1)
        dispatch({ type: fromBlockActions.SURVEY_BLOCK_CREATE, data: newBlock })
        break
      case BlockTypes.BlockMultimedia:
        newBlock = prepareBlockMultimediaData(lessonId, list.length + 1)
        dispatch({
          type: fromBlockActions.MULTIMEDIA_BLOCK_CREATE,
          data: newBlock,
        })
        break
      case BlockTypes.BlockRichText:
        newBlock = prepareBlockRichTextData(lessonId, list.length + 1)
        dispatch({
          type: fromBlockActions.RICHTEXT_BLOCK_CREATE,
          data: newBlock,
        })
        break
      case BlockTypes.BlockTask:
        newBlock = prepareBlockTaskData(lessonId, list.length + 1)
        dispatch({ type: fromBlockActions.TASK_BLOCK_CREATE, data: newBlock })
        break
      case BlockTypes.BlockEvaluation:
        /* newBlock = prepareBlockEvaluationData(lessonId, list.length + 1)
        dispatch({
          type: fromBlockActions.EVALUATION_BLOCK_CREATE,
          data: newBlock,
        }) */
        break
      case BlockTypes.BlockScorm:
        newBlock = prepareBlockScormData(lessonId, list.length + 1)
        dispatch({
          type: fromBlockActions.SCORM_BLOCK_CREATE,
          data: newBlock,
        })
        break
      case BlockTypes.BlockMeeting:
        newBlock = prepareBlockMeetingData(lessonId, list.length + 1)
        dispatch({
          type: fromBlockActions.MEETING_BLOCK_CREATE,
          data: newBlock,
        })
        break
      case BlockTypes.BlockSession:
        dispatch({
          type: fromBlockActions.EDIT_BLOCK,
          payload: {
            lessonId: Number(lessonId),
            order: list.length + 1,
            blockType: BlockTypes.BlockSession,
          },
        })
        break
      default:
        break
    }

    setSelectedOption(option)
    onFinish()
  }

  const handleSubmit = () => {
    const result = order.map(({ id }: IBlockInstanceModel) => id)
    result.length > 1 &&
      dispatch({
        type: fromBlockActions.BLOCK_ORDER_EDIT,
        payload: { blocks: result, lessonId },
      })
    setLoading(true)
    dispatch({
      type: fromLessonActions.LESSON_EDIT,
      payload: { lessonId, lessonData, history },
    })
  }

  const handleEdit = (block: IBlockInstanceModel) => {
    onEdit()
    console.log(block)

    dispatch({
      type: fromBlockActions.EDIT_BLOCK,
      payload: { ...block, instanceId: block.id, id: block.blockId, lessonId },
    })

    setSelectedOption(block.blockType || null)
    onFinish()
  }

  const blockInstance = React.useMemo(
    () => list.find((block) => block.id === selected),
    [list, selected]
  )

  const handleDelete = async () => {
    if (blockInstance) {
      setSelected(0)
      const blockId = blockInstance.blockId
      if (!blockId) return
      switch (blockInstance.blockType) {
        case BlockTypes.BlockSurvey:
          dispatch({
            type: fromBlockActions.DESTROY_SURVEY_BLOCK,
            payload: { blockSurveyId: blockId, lessonId },
          })
          break
        case BlockTypes.BlockRichText:
          dispatch({
            type: fromBlockActions.DESTROY_RICHTEXT_BLOCK,
            payload: { richTextBlockId: blockId, lessonId },
          })
          break
        case BlockTypes.BlockTask:
          dispatch({
            type: fromBlockActions.DESTROY_TASK_BLOCK,
            payload: { taskBlockId: blockId, lessonId },
          })
          break
        case BlockTypes.BlockMultimedia:
          dispatch({
            type: fromBlockActions.DESTROY_MULTIMEDIA_BLOCK,
            payload: { multimediaBlockId: blockId, lessonId },
          })
          break
        case BlockTypes.BlockEvaluation:
          dispatch({
            type: fromBlockActions.DESTROY_EVALUATION_BLOCK,
            payload: { evaluationBlockId: blockId, lessonId },
          })
          break
        case BlockTypes.Evaluation: {
          dispatch(fromBlockActions.Actions.setLoading(true))
          try {
            await destroyEvaluation(blockId)
          } finally {
            dispatch(fromBlockActions.Actions.setLoading(false))
          }
          const data = await lessonBlocksList(+lessonId)
          dispatch(fromBlockActions.Actions.getLessonBlocksListSuccess(data))
          break
        }
        case BlockTypes.BlockScorm:
          dispatch({
            type: fromBlockActions.DESTROY_SCORM_BLOCK,
            payload: { scormBlockId: blockId, lessonId },
          })
          break
        case BlockTypes.BlockMeeting:
          dispatch({
            type: fromBlockActions.DESTROY_MEETING_BLOCK,
            payload: {
              meetingBlockId: blockId,
              lessonId,
            },
          })
          break
        case BlockTypes.BlockSession:
          dispatch({
            type: fromBlockActions.DESTROY_SESSION_BLOCK,
            payload: {
              sessionBlockId: blockId,
              lessonId,
            },
          })
          break
        default:
          break
      }
    }
  }

  const handleSaveOrder = () => {
    const result = order.map(({ id }: IBlockInstanceModel) => id)
    result.length > 1 &&
      dispatch({
        type: fromBlockActions.BLOCK_ORDER_EDIT,
        payload: { blocks: result, lessonId },
      })
    setDraggable(false)
  }

  return {
    handleSelectedOptionChange,
    handleDelete,
    handleSubmit,
    handleEdit,
    handleSaveOrder,
    setOrder,
    setDraggable,
    setSelectedOption,
    setSelected,
    setLessonData,
    states: {
      selectedOption,
      selected,
      loading,
      draggable,
      lessonData,
    },
  }
}

export default useActions
